import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';

import Portal from 'shared/components/Portal';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface ModalContentProps {
  background: string;
  customStyle?: { [key: string]: string };
}

const ModalContent = styled.div`
  position: fixed;
  max-width: 100%;
  background: ${({ background }: ModalContentProps) => background};
  border-radius: 2px;
  z-index: 11;
  border: 1px solid rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 30px 6px rgba(0, 0, 0, 0.15);
  top: 0;
  right: 0;
  height: 100%;

  &.partial-modal {
    top: 50%;
    left: 50%;
    width: 100%;
    transform: translate(-50%, -50%);
    height: auto;
    max-width: 500px;
    overflow: hidden;
    max-width: 500px;
  }
  &.stick-to-bottom {
    top: unset;
    bottom: 0;
    margin: auto;
    max-width: 500px;
    transform: translateX(-50%);
    overflow: hidden;
  }
  ${({ customStyle }: ModalContentProps) => customStyle};
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10;

  .hide-overlay {
    z-index: 1;
    background-color: rgba(0, 0, 0, 0);
  }
`;

interface ModalProps {
  children: React.ReactNode;
  onHide: VoidFunction;
  modalContentClass?: string;
  allowBackdropClick?: boolean;
  modalBackground?: string;
  isVisible?: boolean;
  isPartial?: boolean;
  isStickBottom?: boolean;
  contentStyle?: { [key: string]: string };
  customStyle?: { [key: string]: string };
  ariaLabel?: string;
  ariaLabelledBy?: string;
}

const Modal = ({
  children,
  onHide,
  allowBackdropClick = true,
  modalContentClass = '',
  modalBackground = '#fff',
  isVisible,
  isPartial,
  isStickBottom,
  contentStyle,
  customStyle,
  ariaLabel,
  ariaLabelledBy,
}: ModalProps) => {
  const [isHidden, setIsHidden] = useState(isVisible);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    // Move focus to first focusable element inside dialog
    const frame = requestAnimationFrame(() => {
      if (dialogRef.current) {
        const focusable =
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
        if (focusable.length > 0) {
          focusable[0].focus();
        } else {
          dialogRef.current.focus();
        }
      }
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.style.overflow = 'visible';
      // Return focus to the element that triggered the dialog
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  const hide = useCallback(() => {
    setIsHidden(true);
    onHide();
  }, [onHide]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        hide();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [hide]
  );

  // TODO: Quicker fix is done but need to update this (not able to pass custom class here)
  const modalCustomClass = `${modalContentClass} ${
    isStickBottom ? 'stick-to-bottom' : ''
  } ${isPartial ? 'partial-modal' : ''} `;
  const overlayClass = isHidden ? '' : 'hide-overlay';
  const backdropClick = allowBackdropClick ? hide : () => {};

  return (
    <Portal>
      <ModalContent
        ref={dialogRef}
        className={modalCustomClass}
        background={modalBackground}
        style={contentStyle}
        customStyle={customStyle}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        {children}
      </ModalContent>
      <Overlay
        className={overlayClass}
        onClick={backdropClick}
      />
    </Portal>
  );
};

export default memo(Modal);
